import React, { useEffect, useState } from 'react';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../../hooks/useBrandingColors';
import { publicApiClient } from '../../api/apiClient';
import { loadCompaniesForSelect } from '../../services/companySelectOptionsService';
import { labelCls, CloseIcon } from './companyModalUi';
import {
  ROLE_OPTIONS,
  EMAIL_RE,
  PASSWORD_MIN,
  selectCls,
  inputCls,
  Chevron,
  accountErrorMessage,
  type AssignableRole,
} from './companyUserModalShared';

interface Props {
  /** Компания карточки — в неё по умолчанию добавляется пользователь. */
  companyId: number;
  companyName: string;
  onClose: () => void;
  /** Успешное создание — родитель закрывает модалку и перезагружает таблицу. */
  onSaved: () => void;
}

export const AddCompanyUserModal: React.FC<Props> = ({ companyId, companyName, onClose, onSaved }) => {
  const colors = useBrandingColors();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AssignableRole>('RECRUITER');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(companyId);
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([{ id: companyId, name: companyName }]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Список компаний для дропдауна (суперадмин может выбрать любую). Текущая — по умолчанию.
  useEffect(() => {
    let cancelled = false;
    loadCompaniesForSelect()
      .then(companiesList => {
        if (cancelled) return;
        const list = companiesList.map(c => ({ id: c.id, name: c.name }));
        if (!list.some(c => c.id === companyId)) list.unshift({ id: companyId, name: companyName });
        setCompanies(list);
      })
      .catch(() => {
        /* оставляем хотя бы текущую компанию */
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, companyName]);

  const validate = (): string | null => {
    if (!firstName.trim()) return 'Введите имя';
    if (!lastName.trim()) return 'Введите фамилию';
    if (!email.trim()) return 'Введите почту';
    if (!EMAIL_RE.test(email.trim())) return 'Введите корректный email адрес';
    if (!password) return 'Введите пароль';
    if (password.length < PASSWORD_MIN) return `Пароль должен содержать минимум ${PASSWORD_MIN} символов`;
    if (!selectedCompanyId) return 'Выберите компанию';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await publicApiClient.account.createAccount({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        role: role as any,
        companyId: selectedCompanyId,
      });
      onSaved();
    } catch (err: any) {
      setError(accountErrorMessage(err, 'Не удалось добавить пользователя'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-[540px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto rounded-[24px] bg-[#F5F6F1]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
          {/* Заголовок */}
          <div className="flex items-center justify-between h-8">
            <span className="w-8 shrink-0" />
            <h2 className="flex-1 text-center text-[24px] leading-[32px] font-medium tracking-[-0.015em] text-black">
              Создание нового пользователя
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 shrink-0 flex items-center justify-center text-[#868C98] hover:text-[#0A0D14]"
              aria-label="Закрыть"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex flex-col gap-6">
            {/* Имя + Фамилия */}
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls} htmlFor="add-user-first-name">Имя</label>
                <input
                  id="add-user-first-name"
                  type="text"
                  placeholder="Например: Иван"
                  value={firstName}
                  onChange={e => { setFirstName(e.target.value); setError(null); }}
                  className={inputCls}
                />
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls} htmlFor="add-user-last-name">Фамилия</label>
                <input
                  id="add-user-last-name"
                  type="text"
                  placeholder="Например: Иванов"
                  value={lastName}
                  onChange={e => { setLastName(e.target.value); setError(null); }}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Почта */}
            <div className="flex flex-col gap-3">
              <label className={labelCls} htmlFor="add-user-email">Почта</label>
              <input
                id="add-user-email"
                type="text"
                inputMode="email"
                placeholder="example@mail.ru"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(null); }}
                className={inputCls}
              />
            </div>

            {/* Пароль */}
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-col gap-3">
                <label className={labelCls} htmlFor="add-user-password">Пароль</label>
                <input
                  id="add-user-password"
                  type="text"
                  placeholder="Qwerty123!"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(null); }}
                  className={inputCls}
                />
              </div>
              <p className="text-[12px] leading-[20px] tracking-[-0.006em] text-[#868C98]">
                * Пароль должен быть надежным и содержать не менее {PASSWORD_MIN} символов.
              </p>
            </div>

            {/* Роль + Компания */}
            <div className="flex gap-3">
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls} htmlFor="add-user-role">Роль</label>
                <div className="relative">
                  <select
                    id="add-user-role"
                    value={role}
                    onChange={e => setRole(e.target.value as AssignableRole)}
                    className={selectCls}
                  >
                    {ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <Chevron />
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls} htmlFor="add-user-company">Компания</label>
                <div className="relative">
                  <select
                    id="add-user-company"
                    value={selectedCompanyId}
                    onChange={e => setSelectedCompanyId(Number(e.target.value))}
                    className={`${selectCls} truncate`}
                  >
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <Chevron />
                </div>
              </div>
            </div>

            {error && <p className="text-[14px] text-[#DF1C41]" role="alert">{error}</p>}
          </div>

          {/* Кнопки */}
          <div className="flex justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="w-[113px] h-[52px] rounded-[12px] flex items-center justify-center bg-[#E2E4E9] text-[#525866] text-[20px] leading-7 font-medium hover:bg-[#d8dade] transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-[274px] h-[52px] rounded-[12px] flex items-center justify-center text-white text-[20px] leading-7 font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={getButtonInlineStyles(colors, 'primary')}
              onMouseEnter={e => { if (!loading) Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
              onMouseLeave={e => { if (!loading) Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
            >
              {loading ? 'Добавление...' : 'Добавить пользователя'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCompanyUserModal;
